import store from "@/store";

/**
 * Converts an enum-like object of filter values into an array of objects.
 *
 * Each object contains:
 * - id: the original filter value
 * - label: a human-readable label with the first letter capitalized
 *
 * @param {Object} filters - An object representing filter enums.
 * @returns {Array} Array of objects with `id` and capitalized `label` properties.
 */
function mappedFilters(filters) {
  const filtersAsObjects = Object.values(filters).map((value) => ({
    id: value,
    label: value.charAt(0).toUpperCase() + value.slice(1) // "individual" -> "Individual"
  }));

  return filtersAsObjects;
}

/**
 * Calculates the timestamp of the user's latest completed achievement.
 *
 * It checks all achievements to find those fully completed by the user
 * (all required codes earned), then returns the most recent timestamp among those completions.
 *
 * This timestamp is used as a tie-breaker in leaderboard rankings:
 * users who completed their achievements earlier rank higher when points are equal.
 *
 * If the user hasn’t completed any achievements, returns Infinity to place them last.
 *
 * @param {Array} codes - Array of user codes with their timestamps.
 * @returns {number} Timestamp of latest achievement completion or Infinity if none completed.
 */
function getFinalCompletionTimestamp(codes) {
  let achievementsData = store.getters["content/getAchievements"];
  const achievementCodes = achievementsData.map((a) => ({
    id: a.uid,
    points: a.points,
    codes: a.codes
  }));

  const codeMap = new Map(codes.map((c) => [c.code, c.timestamp]));

  let completionTimestamps = [];

  for (const { codes } of achievementCodes) {
    const timestamps = codes.map((code) => codeMap.get(code));

    if (timestamps.every((ts) => ts !== undefined)) {
      // Achievement completed
      completionTimestamps.push(Math.max(...timestamps));
    }
  }

  return completionTimestamps.length > 0
    ? Math.max(...completionTimestamps)
    : Infinity; // users with no completed achievements go last in tie-break
}

/**
 * Filters a user's achievement codes to only include those updated within the current week (Monday to Sunday).
 * The week is determined based on the current date in the "America/Los_Angeles" timezone.
 *
 * @param {Object} user - User object containing an array of achievement codes with timestamps.
 * @returns {Array} Filtered array of achievement codes updated within the current week.
 */
function filterPointsByWeek(payload) {
  // get today's date in PST
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
  );

  // get day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
  const dayOfWeek = now.getDay();

  // find how many days to subtract to get back to Monday
  // note: if today is Sunday (0), we want to go back 6 days
  const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // get Monday at 00:00:00
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - diffToMonday);
  weekStart.setHours(0, 0, 0, 0);

  // get Sunday at 23:59:59
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  // convert to seconds timestamp
  const WEEK_START_DATE = Math.floor(weekStart.getTime() / 1000);
  const WEEK_END_DATE = Math.floor(weekEnd.getTime() / 1000);

  // then filter
  let filteredPoints;

  let _payload = payload.codes ? payload.codes : payload;

  filteredPoints = _payload.filter(
    (code) =>
      code.tsLastUpdated >= WEEK_START_DATE &&
      code.tsLastUpdated <= WEEK_END_DATE
  );

  return filteredPoints;
}

/**
 * Formats a Unix timestamp (in seconds) into a readable date string.
 *
 * Converts the timestamp to a JavaScript Date object, then formats it
 * as a full date with year, month (long name), and day.
 *
 * @param {number} unixTimestamp - Timestamp in seconds since the Unix epoch.
 * @returns {string} Formatted date string (e.g., "June 24, 2025").
 */
function formatDate(unixTimestamp) {
  const date = new Date(unixTimestamp * 1000); // Convert from seconds to milliseconds
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timezone: "America/Los_Angeles"
    /*hour: "numeric",
    minute: "numeric",
    second: "numeric"
    */
  };
  return date.toLocaleDateString(undefined, options);
}

/**
 * Formats a Unix timestamp (in seconds) into a readable date string.
 *
 * Converts the timestamp to a JavaScript Date object, then formats it
 * as a full date with year, month (long name), and day.
 *
 * @param {number} unixTimestamp - Timestamp in seconds since the Unix epoch.
 * @returns {string} Formatted date string (e.g., "June 24, 2025").
 */
function formatDateTime(unixTimestamp) {
  const date = new Date(unixTimestamp * 1000); // Convert from seconds to milliseconds
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
  };
  return date.toLocaleDateString(undefined, options);
}

/** * Shortens a full name to the format "First LastInitial.".
 *
 * If the name has multiple parts, it returns the first name and the first letter of the last part followed by a period. If only one name is provided, it returns it as is.
 *
 * @param {string} fullName - The full name to shorten.
 * @returns {string} The shortened name in "First L." format.
 */
function shortenName(fullName) {
  if (!fullName) return "";

  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]; // just one name, return as is

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0].toUpperCase();

  return `${firstName} ${lastInitial}.`;
}

export {
  mappedFilters,
  getFinalCompletionTimestamp,
  filterPointsByWeek,
  formatDate,
  formatDateTime,
  shortenName
};
